<template>
  <div
    v-if="user?.id === SystemUser.id"
    class="rounded-full bg-burgundy-900 p-2"
    :class="size"
  >
    <img src="@/assets/Logomark.png" alt="" class="size-full" />
  </div>
  <img
    v-else-if="profilePictureUrl && !imageLoadError"
    :src="profilePictureUrl"
    :alt="user?.name ?? 'Profile picture'"
    class="aspect-square shrink-0 rounded-full object-cover ring-1 ring-purple-200"
    :class="size"
    @error="imageLoadError = true"
  />
  <div
    v-else
    class="flex aspect-square shrink-0 items-center justify-center rounded-full font-medium text-gray-800 ring-1 ring-purple-200"
    :class="[avatarColor, size, fontSize]"
  >
    {{ initials.join("") }}
  </div>
</template>

<script setup lang="ts">
import { PropType, computed, ref, watch } from "vue";
import { SystemUser } from "@/services/user";
import { sum } from "lodash-es";

interface UserWithAvatar {
  id: number;
  name: string;
  profilePictureUrl?: string | null;
}

const props = defineProps({
  user: {
    type: Object as PropType<UserWithAvatar | null>,
    default: null,
  },
  size: { type: String, default: "size-10" },
  fontSize: { type: String, default: "text-sm" },
});

const profilePictureUrl = computed(() => props.user?.profilePictureUrl ?? null);
const imageLoadError = ref(false);

// Reset error state when profile picture URL changes
watch(profilePictureUrl, () => {
  imageLoadError.value = false;
});

const initials = computed(() => {
  if (!props.user?.name) {
    return [];
  }
  const names = props.user.name.split(" ");
  const initials = [names[0][0].toUpperCase()];
  if (names.length > 1 && names[names.length - 1]) {
    initials.push(names[names.length - 1][0].toUpperCase());
  }
  return initials;
});

const avatarColor = computed(() => {
  const colors = [
    "bg-peach-200",
    "bg-purple-500",
    "bg-purple-200",
    "bg-purple-600",
  ];
  return colors[
    sum(initials.value.map((c) => c.charCodeAt(0))) % colors.length
  ];
});
</script>
